import { ExpandLess as CollapseIcon, ExpandMore as ExpandIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import {
  api,
  APIError,
  GuideFactCheck,
  GuideFactCheckBlockingFinding,
  GuideFactCheckCategory,
  GuideFactCheckReviewItem,
} from '../services/api-client';

// The Content API enforces the same bounds (10 to 500 characters, trimmed).
export const FACT_CHECK_REASON_MIN = 10;
export const FACT_CHECK_REASON_MAX = 500;

const CATEGORY_LABELS: Record<GuideFactCheckCategory, string> = {
  closed: 'Closed',
  card_mismatch: 'Wrong card',
  place_mismatch: 'Wrong place',
  contradicted: 'Contradicted',
};

// Sources come from web search results: only ever link http(s).
function safeHref(url: string | undefined): string | null {
  return url && /^https?:\/\//i.test(url) ? url : null;
}

function formatWhen(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function SourceLink({ url }: { url: string }) {
  const href = safeHref(url);
  if (!href) return null;
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" variant="caption">
      Source
    </Link>
  );
}

interface Props {
  slug: string;
  factCheck: GuideFactCheck;
  // Only an editorial reviewer can dismiss or undo; everyone else reads.
  isReviewer: boolean;
  // Called after a dismissal or an undo so the checklist is re-evaluated.
  onChanged: () => Promise<void> | void;
}

/**
 * The fact-check's findings under the gate checklist: what blocks (claim,
 * correction, source), a reviewer's "Mark as false positive" with a required
 * reason, who dismissed what and why (with Undo), and the `review_items` that do
 * not block, collapsed.
 */
export default function GuideFactCheckFindings({ slug, factCheck, isReviewer, onChanged }: Props) {
  const [target, setTarget] = useState<GuideFactCheckBlockingFinding | null>(null);
  const [reason, setReason] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);

  if (!factCheck.present) return null;

  const trimmed = reason.trim();
  const reasonValid = trimmed.length >= FACT_CHECK_REASON_MIN && trimmed.length <= FACT_CHECK_REASON_MAX;

  const messageOf = (err: unknown, fallback: string): string => {
    const e = err as Partial<APIError> | undefined;
    // A 409 carries the Content API's own reason (stale fact-check, already dismissed).
    return (e?.details?.upstream_detail as string | undefined) || e?.message || fallback;
  };

  const openDialog = (finding: GuideFactCheckBlockingFinding) => {
    setError(null);
    setReason('');
    setTarget(finding);
  };

  const closeDialog = () => {
    if (busyId) return;
    setTarget(null);
  };

  const dismiss = async () => {
    if (!target || !reasonValid) return;
    setBusyId(target.findingId);
    setError(null);
    try {
      await api.dismissGuideFactCheckFinding(slug, {
        findingId: target.findingId,
        reason: trimmed,
      });
      setTarget(null);
      await onChanged();
    } catch (err) {
      setError(messageOf(err, 'Failed to dismiss the finding'));
      // The finding may have moved (a re-run, an edit): show the current list.
      await onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const undo = async (finding: GuideFactCheckBlockingFinding) => {
    setBusyId(finding.findingId);
    setError(null);
    try {
      await api.undismissGuideFactCheckFinding(slug, { findingId: finding.findingId });
      await onChanged();
    } catch (err) {
      setError(messageOf(err, 'Failed to undo the dismissal'));
      await onChanged();
    } finally {
      setBusyId(null);
    }
  };

  const { blocking, review_items: reviewItems, counts } = factCheck;

  return (
    <Box sx={{ mt: 1, mb: 1.5, pl: { sm: 11.5 } }} data-testid="fact-check-findings">
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {blocking.length > 0 && (
        <Box data-testid="fact-check-blocking">
          <Typography variant="caption" fontWeight={600} component="div" sx={{ mb: 0.5 }}>
            Blocking findings: {counts.open} open, {counts.dismissed} dismissed
          </Typography>
          {blocking.map((f) => (
            <Box
              key={f.findingId}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.25, mb: 1 }}
              data-testid={`fact-check-finding-${f.findingId}`}
            >
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 0.5 }}>
                <Chip
                  label={CATEGORY_LABELS[f.category] ?? f.category}
                  size="small"
                  color={f.dismissed ? 'default' : 'error'}
                  variant={f.dismissed ? 'outlined' : 'filled'}
                />
                <Typography variant="caption" color="text.secondary">
                  {f.venue}
                </Typography>
              </Box>
              <Typography variant="body2" fontWeight={600}>
                {f.claim}
              </Typography>
              {f.correction && (
                <Typography variant="caption" component="div" color="text.secondary">
                  Correction: {f.correction} <SourceLink url={f.source_url} />
                </Typography>
              )}
              {!f.correction && safeHref(f.source_url) && <SourceLink url={f.source_url} />}

              {f.dismissed && f.dismissal && (
                <Alert
                  severity="info"
                  sx={{ mt: 1 }}
                  action={
                    isReviewer ? (
                      <Button
                        color="inherit"
                        size="small"
                        disabled={busyId === f.findingId}
                        onClick={() => undo(f)}
                      >
                        {busyId === f.findingId ? <CircularProgress size={14} /> : 'Undo'}
                      </Button>
                    ) : undefined
                  }
                >
                  Dismissed as a false positive by {f.dismissal.reviewer}
                  {f.dismissal.at ? ` on ${formatWhen(f.dismissal.at)}` : ''}: {f.dismissal.reason}
                </Alert>
              )}

              {!f.dismissed && f.dismissalExpired && f.dismissal && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {f.dismissal.reviewer} dismissed this on {formatWhen(f.dismissal.at)}, but the
                  content has changed since, so it blocks again. Check it again and dismiss it
                  again if it is still a false positive.
                </Alert>
              )}

              {!f.dismissed && isReviewer && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  onClick={() => openDialog(f)}
                  disabled={busyId !== null}
                >
                  Mark as false positive
                </Button>
              )}
            </Box>
          ))}
          {!isReviewer && counts.open > 0 && (
            <Typography variant="caption" color="text.secondary" component="div">
              Only an editorial reviewer can mark a finding as a false positive.
            </Typography>
          )}
        </Box>
      )}

      {reviewItems.length > 0 && (
        <Box sx={{ mt: blocking.length > 0 ? 1 : 0 }} data-testid="fact-check-review-items">
          <Button
            size="small"
            color="inherit"
            onClick={() => setReviewOpen((open) => !open)}
            endIcon={reviewOpen ? <CollapseIcon /> : <ExpandIcon />}
            sx={{ textTransform: 'none', px: 0 }}
            aria-expanded={reviewOpen}
          >
            For review (not blocking) ({reviewItems.length})
          </Button>
          <Collapse in={reviewOpen} unmountOnExit>
            {reviewItems.map((item: GuideFactCheckReviewItem) => (
              <Box key={item.findingId} sx={{ py: 0.75 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Chip label={item.type || item.level || 'review'} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary">
                    {item.venue}
                  </Typography>
                </Box>
                <Typography variant="body2">{item.claim}</Typography>
                {(item.correction || item.reason) && (
                  <Typography variant="caption" component="div" color="text.secondary">
                    {item.correction ? `Correction: ${item.correction}` : item.reason}{' '}
                    <SourceLink url={item.source_url} />
                  </Typography>
                )}
              </Box>
            ))}
          </Collapse>
        </Box>
      )}

      <Dialog open={target !== null} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Mark as false positive</DialogTitle>
        <DialogContent>
          {target && (
            <>
              <Typography variant="body2" fontWeight={600}>
                {target.claim}
              </Typography>
              <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 2 }}>
                {target.venue}
                {target.correction ? ` · Correction: ${target.correction}` : ''}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                Dismissing this lets the fact-check pass without it. It is recorded under your name
                with your reason, and it expires if the guide's content changes.
              </Typography>
              {error && (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  {error}
                </Alert>
              )}
              <TextField
                label="Reason (what you checked, and where)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                required
                autoFocus
                inputProps={{ maxLength: FACT_CHECK_REASON_MAX }}
                helperText={`${trimmed.length}/${FACT_CHECK_REASON_MAX} characters, at least ${FACT_CHECK_REASON_MIN}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={busyId !== null}>
            Cancel
          </Button>
          <Button variant="contained" onClick={dismiss} disabled={!reasonValid || busyId !== null}>
            {busyId !== null ? <CircularProgress size={18} /> : 'Mark as false positive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
